import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PremiumBadge } from "@/components/PremiumBadge";
import { Flame, Video, MessageCircle, MapPin, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending_call: { label: "Vibe check pending", cls: "bg-amber-500/20 text-amber-200" },
  call_in_progress: { label: "Call in progress", cls: "bg-amber-500/20 text-amber-200" },
  deciding: { label: "Awaiting decision", cls: "bg-pink-500/20 text-pink-200" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-500/20 text-emerald-200" },
  closed: { label: "Closed", cls: "bg-white/10 text-slate-400" },
};

export default function Matches() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: matches = [], isLoading } = useQuery({
    queryKey: ["matches"],
    queryFn: async () => (await api.get("/matches")).data,
    refetchInterval: 6000,
  });

  const nudge = async (m) => {
    try {
      const { data } = await api.post(`/matches/${m.id}/nudge`);
      if (data.reopened) navigate(`/call/${m.id}`);
      else { toast.success(`Nudge sent to ${m.partner?.name} 💌`); qc.invalidateQueries({ queryKey: ["matches"] }); }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send nudge"); }
  };

  const active = matches.filter((m) => m.status !== "closed");
  const closed = matches.filter((m) => m.status === "closed");

  return (
    <div className="fade-up">
      <h1 className="font-display font-black text-4xl">Your matches</h1>
      <p className="text-slate-300 mt-1">Every match starts with a 60-second vibe check.</p>

      {isLoading ? (
        <div className="space-y-3 mt-6">{[...Array(3)].map((_, i) => <div key={i} className="h-24 rounded-2xl bg-white/5 animate-pulse" />)}</div>
      ) : matches.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center mt-10">
          <Flame className="h-10 w-10 text-amber-400/60 mx-auto mb-3" />
          <p className="text-slate-300">No matches yet. Head to an event and tell someone you want to go together.</p>
          <Button data-testid="matches-browse" onClick={() => navigate("/discover")} className="btn-gold rounded-full h-11 px-6 mt-5">Browse events</Button>
        </div>
      ) : (
        <div className="space-y-3 mt-6">
          {active.map((m) => (
            <div key={m.id} data-testid={`match-${m.id}`}
              onClick={() => navigate(m.status === "confirmed" ? `/chat/${m.id}` : `/call/${m.id}`)}
              className="glass rounded-2xl p-3 flex items-center gap-4 fade-up cursor-pointer hover:border-amber-500/40 transition-colors">
              <div className="relative h-20 w-20 rounded-xl overflow-hidden shrink-0">
                <img src={imgUrl(m.partner?.photos?.[0] || m.partner?.picture)} alt={m.partner?.name} className="h-full w-full object-cover" />
                {m.partner?.online && <span className="absolute bottom-1 right-1 h-3.5 w-3.5 rounded-full bg-emerald-400 ring-2 ring-[#0B130E]" title="Online now" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-display font-bold text-lg truncate">{m.partner?.name}</h3>
                  {m.partner?.premium && <PremiumBadge size="xs" showLabel={false} />}
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS[m.status]?.cls}`}>{STATUS[m.status]?.label}</span>
                </div>
                <p className="text-xs text-slate-300 flex items-center gap-1.5 mt-1 truncate"><MapPin className="h-3 w-3" /> {m.event?.title}</p>
              </div>
              {m.status === "confirmed" ? (
                <Button data-testid={`open-chat-${m.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/chat/${m.id}`); }} className="btn-gold rounded-full h-10 px-4 shrink-0">
                  <MessageCircle className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Chat</span>
                </Button>
              ) : (
                <Button data-testid={`open-call-${m.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/call/${m.id}`); }} className="btn-pink rounded-full h-10 px-4 shrink-0">
                  <Video className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Vibe check</span>
                </Button>
              )}
            </div>
          ))}

          {closed.length > 0 && (
            <>
              <p className="text-xs uppercase tracking-widest text-slate-400 pt-4">Closed</p>
              {closed.map((m) => (
                <div key={m.id} className={`glass rounded-2xl p-3 flex items-center gap-4 ${m.nudge_incoming ? "border-amber-400/40" : ""}`}>
                  <img src={imgUrl(m.partner?.photos?.[0] || m.partner?.picture)} alt="" className="h-14 w-14 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate text-slate-100 flex items-center gap-2">{m.partner?.name} {m.partner?.premium && <PremiumBadge size="xs" showLabel={false} />}</h3>
                    <p className="text-xs text-slate-400 truncate">
                      {m.nudge_incoming ? "Wants a rematch 🔁" : m.nudge_outgoing ? "Rematch invite sent" : m.event?.title}
                    </p>
                  </div>
                  {m.nudge_incoming ? (
                    <Button data-testid={`respond-${m.id}`} onClick={() => navigate(`/call/${m.id}`)} className="btn-pink rounded-full h-9 px-4 shrink-0">Respond</Button>
                  ) : m.nudge_outgoing ? (
                    <span className="text-xs text-amber-300/80 shrink-0">Sent ✓</span>
                  ) : (m.rematch_count ?? 0) < (m.rematch_max ?? 2) ? (
                    <Button data-testid={`nudge-${m.id}`} onClick={() => nudge(m)} variant="outline" size="sm" className="rounded-full h-9 border-amber-500/30 text-amber-300 shrink-0">
                      <RotateCcw className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Nudge</span>
                    </Button>
                  ) : (
                    <span className="text-xs text-slate-400 shrink-0">Closed</span>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
